/**
 * Remembers how a learner likes their careers ordered.
 *
 * The URL still wins — a shared or bookmarked link must open exactly the list
 * it promised. Storage only answers when the URL says nothing, so coming back
 * to the Explorer tomorrow picks up where you left off instead of resetting to
 * A–Z, and a link someone sent you is never quietly rewritten by your habits.
 *
 * Display-only. Nothing here scores or is sent anywhere.
 */
import { isCareerSortKey, type CareerSortKey } from "@/lib/careerContent";

/**
 * Device-local on purpose, and deliberately outside the `wcc360.` namespace:
 * that prefix is harvested into the synced cloud store, which hydrates after
 * first paint. A list order has to be known the instant the page mounts, so it
 * stays on the device.
 */
const KEY = "wekit.careers.sort.v1";

/** The stored preference, or null when there isn't a usable one. */
export function readSortPref(): CareerSortKey | null {
  try {
    const raw = localStorage.getItem(KEY);
    return isCareerSortKey(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Best-effort write: a blocked or full storage must never break the list. */
export function writeSortPref(sort: CareerSortKey): void {
  try {
    localStorage.setItem(KEY, sort);
  } catch {
    /* private mode, quota, or no storage at all — the list still works */
  }
}

export function clearSortPref(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clean up */
  }
}

/**
 * Resolve the sort to open with: URL parameter, then remembered preference,
 * then the default. `fallback` is the app's default order.
 */
export function resolveInitialSort(
  fromUrl: string | null,
  fallback: CareerSortKey = "demand_desc",
): CareerSortKey {
  if (isCareerSortKey(fromUrl)) return fromUrl;
  return readSortPref() ?? fallback;
}
